import type { FundDetail } from "@/data/fund-detail-types";

const DISPLAY_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatDisplayDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${DISPLAY_MONTHS[Number.parseInt(m!, 10) - 1]} ${Number.parseInt(d!, 10)}, ${y}`;
}

function buildCapTooltips(expenseRatio: string): Record<string, string> {
  const netTooltip = expenseRatio
    ? `The maximum return the Fund is designed to provide over the full Outcome Period after deduction of estimated Fund fees and expenses. The net cap reflects estimated Outcome Period expenses based on the Fund\u2019s ${expenseRatio} net expense ratio.`
    : "The maximum return the Fund is designed to provide over the full Outcome Period after deduction of estimated Fund fees and expenses.";
  return {
    "Starting Cap (Gross)":
      "The maximum return the Fund is designed to provide over the full Outcome Period before deduction of Fund fees and expenses. The gross cap is set at the beginning of the Outcome Period and may vary for each new Outcome Period.",
    "Starting Cap (Net)": netTooltip,
  };
}

export function BufferOutcomeDetails({ fund }: { fund: FundDetail }) {
  const CAP_TOOLTIPS = buildCapTooltips(fund.expenseRatio);

  const fmtCap = (v: number | undefined): string =>
    v != null && Number.isFinite(v) ? `${v.toFixed(2)}%` : "TBD";

  const useTyped =
    fund.startingCapGrossPct != null ||
    fund.startingCapNetPct != null ||
    !fund.bufferOutcomeDetails;

  const details = useTyped
    ? [
        {
          label: "Series",
          value: fund.seriesMonth || "—",
          highlight: true,
        },
        {
          label: "Period",
          value:
            fund.outcomePeriodStart && fund.outcomePeriodEnd
              ? `${formatDisplayDate(fund.outcomePeriodStart)} – ${formatDisplayDate(fund.outcomePeriodEnd)}`
              : "TBD",
        },
        ...(fund.rebalanceFrequency
          ? [{ label: "Rebalance Frequency", value: fund.rebalanceFrequency }]
          : []),
        { label: "Starting Cap (Gross)", value: fmtCap(fund.startingCapGrossPct) },
        { label: "Starting Cap (Net)", value: fmtCap(fund.startingCapNetPct) },
        {
          label: "Starting Buffer",
          value: fund.bufferLevel
            ? fund.bufferStart && fund.bufferStart > 0
              ? `${fund.bufferLevel.toFixed(2)}% (−${fund.bufferStart}% to −${fund.bufferStart + fund.bufferLevel}%)`
              : `${fund.bufferLevel.toFixed(2)}%`
            : "TBD",
          highlight: true,
        },
        { label: "Underlying ETF", value: fund.referenceTicker ?? "—" },
        ...(fund.optionsType ? [{ label: "Options Type", value: fund.optionsType }] : []),
      ]
    : fund.bufferOutcomeDetails!;

  const calloutText = fund.strategyCallout ?? null;

  return (
    <>
      <div
        data-fund-section="bufferConfig"
        className="border-b border-white/[0.13] px-7 max-md:px-5 max-[480px]:px-4 pt-10 max-md:pt-7 pb-10 max-md:pb-7"
      >
        <h3 className="font-[family-name:var(--font-instrument)] text-xs leading-[1.5] tracking-[0.16em] uppercase text-[var(--etf-white2)] mb-5 flex items-center gap-2.5">
          Outcome Details
          <span className="flex-1 h-px bg-white/[0.13]" />
        </h3>
        <div>
          {details.map((d) => {
            const tooltip = CAP_TOOLTIPS[d.label];
            return (
              <div
                key={d.label}
                className={`flex justify-between items-baseline py-[9px] border-b border-[var(--etf-border2)] gap-3 last:border-b-0 flex-wrap${tooltip ? " group relative" : ""}`}
              >
                <span className="font-[family-name:var(--font-instrument)] text-[11px] leading-[1.5] text-[var(--etf-white2)] tracking-[0.03em] shrink-0 flex items-center gap-1">
                  {d.label}
                  {tooltip && (
                    <span className="text-[13px] leading-none text-[var(--etf-white3)] group-hover:text-[var(--etf-orange)] transition-colors duration-150 cursor-help select-none ml-1">
                      &#9432;
                    </span>
                  )}
                </span>
                <span
                  className={`font-[family-name:var(--font-instrument)] text-right text-[11.5px] leading-[1.5] min-w-0 break-words ${d.highlight ? "text-[var(--etf-orange)]" : "text-[var(--etf-white)]"}`}
                >
                  {d.value}
                </span>
                {tooltip && (
                  <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-150 absolute left-0 top-full mt-1 z-50 w-full bg-[#1a1a1a] border border-[var(--etf-border)] rounded p-3 text-[10.5px] text-[var(--etf-white2)] leading-[1.55] font-[family-name:var(--font-instrument)] shadow-lg">
                    {tooltip}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {calloutText && (
        <div className="border-l-2 border-l-[var(--etf-orange)] bg-[rgba(255,85,0,0.03)] relative flex-1 px-7 max-md:px-5 max-[480px]:px-4 py-10 max-md:py-7">
          <div className="text-[var(--etf-orange)] opacity-80 mb-2.5">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </div>
          <div className="font-[family-name:var(--font-geist)] text-[11px] tracking-[0.14em] uppercase text-[var(--etf-orange)] font-semibold mb-2.5">
            FLEX Options-Based Strategy
          </div>
          <div className="font-[family-name:var(--font-geist)] text-xs text-[var(--etf-white2)] leading-[1.6] font-light">
            {calloutText}
          </div>
        </div>
      )}
    </>
  );
}
